// src/components/EventLimitations.js
import { collection, query, where, getDocs, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase/firebaseConfig';

// LEGACY: per-plan event limits (not used in credit model, kept for compatibility)
const eventLimits = {
  'price_1R85uFRtiroMBcDI97u58KNs': 1, // Event Flash
  'price_1RBQRIRtiroMBcDIUchpI0FF': 1, // Event Glow-Up (new)
  'price_1R85QVRtiroMBcDIy7rg25mQ': 1, // Event Glow-Up (old)
  'price_1R85VSRtiroMBcDIXv9u0P6q': 1, // Social Spark
  'price_1R85YsRtiroMBcDIi5iHkGUb': 1, // VIP Experience
  'price_1R85wBRtiroMBcDIf4HpIbr7': 1,  // Premium
    'price_1SZHdCRtiroMBcDIY5gWTouf': 1, // Cyber Monday Unlimited
    'price_1Su18IRtiroMBcDIHLnrAM3s': 1,
    // Inside eventLimits
'price_1UWiw6RtiroMBcDIIz7RoMNp': 1, // Holiday Special $34.99

  

};


const planPriceIds = [
  'price_1R85uFRtiroMBcDI97u58KNs',
  'price_1RBQRIRtiroMBcDIUchpI0FF',
  'price_1R85QVRtiroMBcDIy7rg25mQ',
  'price_1R85VSRtiroMBcDIXv9u0P6q',
  'price_1R85YsRtiroMBcDIi5iHkGUb',
  'price_1R85wBRtiroMBcDIf4HpIbr7',
  'price_1Su18IRtiroMBcDIHLnrAM3s',
  'price_1SZHdCRtiroMBcDIY5gWTouf',

  // Holiday Special - $34.99
  'price_1UWiw6RtiroMBcDIIz7RoMNp',
];


// Enhanced: credit-per-checkout model
// Counts checkout_sessions with recognized price IDs as event "credits".
// If your docs include a status (e.g., payment_status: 'paid' | 'complete' | 'succeeded'),
// we prefer counting only those; otherwise we fall back to counting all matching sessions.
export const canUserCreateEvent = async () => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return { ok: false, reason: 'unauthenticated' };

  const userRef = doc(db, 'users', user.uid);

  try {
    // Get all sessions with valid price IDs
    const sessionsRef = collection(db, `users/${user.uid}/checkout_sessions`);
    const qBase = query(sessionsRef, where('price', 'in', planPriceIds));
    const qs = await getDocs(qBase);

    const docs = qs.docs;

    // Prefer only paid/completed sessions if the field exists on your docs
    const paidDocs = docs.filter((d) => {
      const data = d.data() || {};
      const status =
        data.payment_status ||
        data.status ||
        data.checkout_status ||
        data.subscription_status ||
        ''; // normalize various possible fields
      const normalized = String(status).toLowerCase();
      return normalized === 'paid' || normalized === 'succeeded' || normalized === 'complete' || normalized === 'completed';
    });

    const creditSource = paidDocs.length ? paidDocs : docs;
    const totalCredits = creditSource.length;

    if (totalCredits === 0) {
      return { ok: false, reason: 'noPayment', used: 0, limit: 0, remaining: 0 };
    }

    // Compute latest priceId (best effort)
    const sorted = [...creditSource].sort((a, b) => {
      const ad = a.data() || {};
      const bd = b.data() || {};
      const at = ad.createdAt?.toMillis?.() ?? ad.createdAt?._seconds * 1000 ?? 0;
      const bt = bd.createdAt?.toMillis?.() ?? bd.createdAt?._seconds * 1000 ?? 0;
      return bt - at;
    });
    const latestPriceId = sorted[0]?.data()?.price || null;

    // How many events already created by this user
    const userDoc = await getDoc(userRef);
    const used = userDoc.exists() ? userDoc.data().eventCount || 0 : 0;

    if (used >= totalCredits) {
      return {
        ok: false,
        reason: 'limitReached',
        used,
        limit: totalCredits,
        remaining: 0,
        priceId: latestPriceId
      };
    }

    return {
      ok: true,
      used,
      limit: totalCredits,
      remaining: totalCredits - used,
      priceId: latestPriceId
    };
  } catch (error) {
    console.error('Error checking event limit:', error.message);
    return { ok: false, reason: 'error' };
  }
};

// Create the event and increment the user's event count
export const createEvent = async (eventData) => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    console.error('User not authenticated');
    return;
  }

  const userId = user.uid;
  const userRef = doc(db, 'users', userId);

  try {
    const userDoc = await getDoc(userRef);
    const currentEventCount = userDoc.exists() ? userDoc.data().eventCount || 0 : 0;

    const eventRef = doc(db, 'events', eventData.uniqueUrl);
    await setDoc(eventRef, eventData);

    // ensure the user doc exists, then update the count
    if (!userDoc.exists()) {
      await setDoc(userRef, { eventCount: 1 }, { merge: true });
    } else {
      await updateDoc(userRef, { eventCount: currentEventCount + 1 });
    }

    console.log('Event created successfully');
  } catch (error) {
    console.error('Error creating event:', error.message);
    alert('Failed to create the event. Please try again.');
  }
};

export { planPriceIds, eventLimits };